"use client";

/**
 * EncryptionModals Component
 *
 * Wrapper component that renders the appropriate encryption modal
 * based on the current encryption state.
 */
import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useEncryption } from '@/contexts/EncryptionContext';
import { useAuthSession } from '@/hooks/useAuthSession';
import { unlockDataKey } from '@/lib/encryption';
import type { EncryptionKeyBundle, RecoveryCodeBundle } from '@/lib/encryption';
import EncryptionSetupModal from './EncryptionSetupModal';
import EncryptionUnlockModal from './EncryptionUnlockModal';

// Pages where encryption modals should NOT be shown
const EXCLUDED_PATHS = ['/auth', '/auth/reset-password', '/auth/callback'];
export default function EncryptionModals() {
  const $ = _c(30);
  const pathname = usePathname();
  const {
    user
  } = useAuthSession();
  const [unlockCancelled, setUnlockCancelled] = useState(false);
  const {
    state,
    showSetupModal,
    showUnlockModal,
    setShowSetupModal,
    setShowUnlockModal,
    saveEncryptionSetup,
    markRecoveryCodeUsed,
    setDataKey
  } = useEncryption();
  let t0;
  if ($[0] !== saveEncryptionSetup || $[1] !== setShowSetupModal) {
    t0 = async (keyBundle, recoveryBundle, deliveryMethod) => {
      await saveEncryptionSetup(keyBundle, recoveryBundle, deliveryMethod);
      setShowSetupModal(false);
    };
    $[0] = saveEncryptionSetup;
    $[1] = setShowSetupModal;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const handleSetupComplete = t0;
  let t1;
  if ($[3] !== markRecoveryCodeUsed || $[4] !== setDataKey || $[5] !== state.recoveryBundle) {
    t1 = async (dataKey, updatedRecoveryBundle) => {
      setDataKey(dataKey);
      if (updatedRecoveryBundle && state.recoveryBundle) {
        const usedCodeHash = updatedRecoveryBundle.usedCodes[updatedRecoveryBundle.usedCodes.length - 1];
        await markRecoveryCodeUsed(updatedRecoveryBundle, usedCodeHash);
      }
    };
    $[3] = markRecoveryCodeUsed;
    $[4] = setDataKey;
    $[5] = state.recoveryBundle;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const handleUnlock = t1;
  if (state.isLoading) {
    return null;
  }
  if (pathname && EXCLUDED_PATHS.some(path => pathname.startsWith(path))) {
    return null;
  }
  let t2;
  if ($[7] !== setShowSetupModal) {
    t2 = () => setShowSetupModal(false);
    $[7] = setShowSetupModal;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = user?.email;
  let t4;
  if ($[9] !== handleSetupComplete || $[10] !== showSetupModal || $[11] !== t2 || $[12] !== t3) {
    t4 = <EncryptionSetupModal isOpen={showSetupModal} onClose={t2} onComplete={handleSetupComplete} userEmail={t3} />;
    $[9] = handleSetupComplete;
    $[10] = showSetupModal;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== handleUnlock || $[15] !== setShowUnlockModal || $[16] !== showUnlockModal || $[17] !== state.keyBundle || $[18] !== state.recoveryBundle) {
    t5 = state.keyBundle && <EncryptionUnlockModal isOpen={showUnlockModal} keyBundle={state.keyBundle} recoveryBundle={state.recoveryBundle} onUnlock={handleUnlock} onCancel={() => {
      setShowUnlockModal(false);
      setUnlockCancelled(true);
    }} />;
    $[14] = handleUnlock;
    $[15] = setShowUnlockModal;
    $[16] = showUnlockModal;
    $[17] = state.keyBundle;
    $[18] = state.recoveryBundle;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== setShowUnlockModal || $[21] !== showUnlockModal || $[22] !== state.isUnlocked || $[23] !== state.keyBundle || $[24] !== unlockCancelled) {
    t6 = unlockCancelled && !showUnlockModal && state.keyBundle && !state.isUnlocked && <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50"><div className="rounded-lg border border-amber-500/30 bg-amber-500/10 backdrop-blur-sm px-6 py-4 shadow-lg"><div className="flex items-center gap-4"><svg className="h-5 w-5 text-amber-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg><div className="text-sm"><p className="text-amber-200 font-medium">Encryption not unlocked</p><p className="text-amber-300/70">Your messages are encrypted and cannot be read.</p></div><div className="flex gap-2 ml-4"><button onClick={() => {
              setUnlockCancelled(false);
              setShowUnlockModal(true);
            }} className="rounded-md bg-amber-500 px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-amber-400 transition-colors">Try Again</button><button onClick={_temp} className="rounded-md border border-amber-500/50 px-3 py-1.5 text-sm font-medium text-amber-300 hover:bg-amber-500/20 transition-colors">Refresh Page</button></div></div></div></div>;
    $[20] = setShowUnlockModal;
    $[21] = showUnlockModal;
    $[22] = state.isUnlocked;
    $[23] = state.keyBundle;
    $[24] = unlockCancelled;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== t4 || $[27] !== t5 || $[28] !== t6) {
    t7 = <>{t4}{t5}{t6}</>;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  return t7;
}
function _temp() {
  return window.location.reload();
}